import { ChangeEvent, useMemo } from "react";
import { FilterIcon } from "lucide-react";

import {
  Column,
  FilterSubMenuMultiselect,
  FilterSubMenuSwitch,
  Row,
} from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";

import {
  MAX_COMMUTE_FILTER_MINUTES,
  SECONDS_PER_MINUTE,
} from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { locations } from "@/lib/locations";
import { cn } from "@/lib/utils";
import { appearanceStore } from "@/stores/appearance";

import { CandidacySource, PipelineFilterAndSortState } from "@/types";

export const JobPipelineFilterPopover = ({
  allLocations,
  allSources,
  setIncludeCouldNotContact,
  setIncludeEmploymentEnded,
  setLocations,
  setMaxCommuteSeconds,
  setSources,
  setStevieCallCompleted,
  state,
}: {
  allLocations: string[];
  allSources: CandidacySource[];
  setIncludeCouldNotContact: (next: boolean) => void;
  setIncludeEmploymentEnded: (next: boolean) => void;
  setLocations: (next: string[]) => void;
  setMaxCommuteSeconds: (next: number | null) => void;
  setSources: (next: CandidacySource[]) => void;
  setStevieCallCompleted: (next: boolean) => void;
  state: PipelineFilterAndSortState;
}) => {
  /* Derived */
  const activeCount = countActivePipelineFilters(state);
  const hasActive = activeCount > 0;
  const locationOptions = useMemo(
    () => allLocations.map((loc) => ({ value: loc, label: loc })),
    [allLocations],
  );
  const sourceOptions = useMemo(
    () =>
      allSources
        .map((source) => ({
          value: source,
          label: formatting.candidacySourceLabel(source),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allSources],
  );

  /* Render */
  const render = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label={
            hasActive ? `Filters (${activeCount.toString()} active)` : "Filters"
          }
          className={cn(
            "relative h-9 w-9 rounded-full border-slate-200 bg-white p-0 shadow-none",
            hasActive
              ? "border-primary-400 bg-primary-50 text-primary-900 hover:bg-primary-100"
              : "text-slate-700 hover:bg-slate-50",
          )}
          size="icon"
          variant="outline"
        >
          <FilterIcon className="h-4 w-4" />
          {hasActive && (
            <Badge className="absolute -right-1 -top-1 h-4 min-w-4 justify-center rounded-full border-white bg-primary-900 px-1 text-[10px] font-semibold leading-none text-white">
              {activeCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[280px] p-0">
        <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Filters
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="mt-0" />
        <FilterSubMenuMultiselect
          label="Source"
          options={sourceOptions}
          selected={state.sources}
          setSelected={setSources}
        />
        <FilterSubMenuMultiselect
          label="Location"
          options={locationOptions}
          searchable
          selected={state.locations}
          setSelected={setLocations}
        />
        <FilterSubMenuSwitch
          checked={state.stevieCallCompleted}
          label="Stevie Call Completed"
          onCheckedChange={setStevieCallCompleted}
        />
        <MaxCommuteSlider
          onChange={setMaxCommuteSeconds}
          value={state.maxCommuteSeconds}
        />
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Includes
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="mt-0" />
        <FilterSubMenuSwitch
          checked={state.includeCouldNotContact}
          label="Could not contact"
          onCheckedChange={setIncludeCouldNotContact}
        />
        <FilterSubMenuSwitch
          checked={state.includeEmploymentEnded}
          label="Employment ended"
          onCheckedChange={setIncludeEmploymentEnded}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return render();
};

const MaxCommuteSlider = ({
  onChange,
  value,
}: {
  onChange: (next: number | null) => void;
  value: number | null;
}) => {
  /* React Hooks */
  const isDarkMode = appearanceStore((state) => state.isDarkMode);

  /* Helpers */
  const maxMinutes = MAX_COMMUTE_FILTER_MINUTES;
  // null = no cap → thumb sits at the far right.
  const selectedMinutes =
    value === null ? maxMinutes : Math.round(value / SECONDS_PER_MINUTE);
  const fillPercent = (selectedMinutes / maxMinutes) * 100;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const minutes = Number(e.target.value);
    // Fully right includes everyone; anything less caps the commute.
    onChange(minutes >= maxMinutes ? null : minutes * SECONDS_PER_MINUTE);
  };

  const render = () => (
    <Column className="gap-2 px-2 py-2.5">
      <Row className="items-center justify-between">
        <Label className="font-normal">Max commute</Label>
        <Label className="font-normal text-slate-500">
          {value === null
            ? "Any"
            : `≤ ${locations.formatCommuteDuration(value)}`}
        </Label>
      </Row>
      <input
        className={cn(
          "h-1.5 w-full cursor-pointer appearance-none rounded-full",
          "[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary-600 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow dark:[&::-webkit-slider-thumb]:border-[#6F8FAA]",
          "[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary-600 [&::-moz-range-thumb]:bg-white dark:[&::-moz-range-thumb]:border-[#6F8FAA]",
        )}
        max={maxMinutes}
        min={0}
        onChange={handleChange}
        step={1}
        style={{
          background: isDarkMode
            ? `linear-gradient(to right, #6F8FAA ${fillPercent.toString()}%, #4C4F52 ${fillPercent.toString()}%)`
            : `linear-gradient(to right, hsl(var(--color-primary-600)) ${fillPercent.toString()}%, rgb(226 232 240) ${fillPercent.toString()}%)`,
        }}
        type="range"
        value={selectedMinutes}
      />
    </Column>
  );

  return render();
};

const countActivePipelineFilters = (
  state: PipelineFilterAndSortState,
): number => {
  let n = 0;
  if (state.sources.length > 0) n += 1;
  if (state.locations.length > 0) n += 1;
  if (state.stevieCallCompleted) n += 1;
  if (state.maxCommuteSeconds !== null) n += 1;
  if (state.includeCouldNotContact) n += 1;
  if (state.includeEmploymentEnded) n += 1;
  return n;
};
